import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { API } from "../config/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import AuthForm from "../components/shared/AuthForm";
import { Gift, Sparkles } from "lucide-react";

// Public landing for an outbound non-user gift magic-link.
// Anyone can view the preview; only a signed-in user can claim.
const GiftClaimPage = () => {
  const { token: claimToken } = useParams();
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null); // {credits_added, from}
  const [showAuth, setShowAuth] = useState(false);
  const [authIsLogin, setAuthIsLogin] = useState(false); // default to sign-up — claim flow attracts new users

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/gifts/claim/${claimToken}`);
        if (!cancelled) setGift(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "This gift link is invalid or already used.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [claimToken]);

  const handleClaim = async () => {
    if (!token) { setShowAuth(true); return; }
    setClaiming(true);
    try {
      const res = await axios.post(`${API}/gifts/claim/${claimToken}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setClaimed(res.data);
      toast.success(`+${res.data.credits_added} credits added to your account!`);
      await refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not claim this gift");
    } finally {
      setClaiming(false);
    }
  };

  // Auto-claim once the user signs in (they were sent here by a friend)
  useEffect(() => {
    if (token && gift && gift.status === "sent" && !claimed && !claiming) {
      handleClaim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, gift]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-950/30 via-gray-950 to-black flex items-center justify-center px-4 py-10" data-testid="gift-claim-page">
      <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto bg-pink-600/20 border border-pink-500/40 rounded-full w-16 h-16 flex items-center justify-center mb-2">
            <Gift className="h-8 w-8 text-pink-300" />
          </div>
          <CardTitle className="text-white text-2xl" data-testid="gift-claim-title">You've got a gift!</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {loading && <p className="text-gray-400">Loading…</p>}

          {error && (
            <>
              <p className="text-rose-300" data-testid="gift-claim-error">{error}</p>
              <Link to="/"><Button variant="outline" className="border-gray-700 text-gray-200">Go home</Button></Link>
            </>
          )}

          {gift && !error && !claimed && (
            <>
              <p className="text-gray-300">
                <span className="font-semibold text-pink-300">@{gift.sender_username}</span> sent you{" "}
                <span className="font-semibold text-emerald-300">{gift.amount_credits} credits</span> to fuel your creativity on GlobalEyes.
              </p>
              {gift.message && (
                <p className="bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-300 text-sm italic" data-testid="gift-message">"{gift.message}"</p>
              )}
              {gift.status !== "sent" && (
                <p className="text-amber-300 text-sm">This gift was already {gift.status}.</p>
              )}
              {gift.status === "sent" && (
                user ? (
                  <Button onClick={handleClaim} disabled={claiming} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="claim-gift-btn">
                    {claiming ? "Claiming…" : `Claim ${gift.amount_credits} credits`}
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => { setAuthIsLogin(false); setShowAuth(true); }} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="claim-signup-btn">
                      Create an account to claim
                    </Button>
                    <button onClick={() => { setAuthIsLogin(true); setShowAuth(true); }} className="text-emerald-400 hover:underline text-sm" data-testid="claim-signin-link">
                      Already have an account? Sign in
                    </button>
                  </>
                )
              )}
            </>
          )}

          {claimed && (
            <div data-testid="claim-success">
              <Sparkles className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
              <p className="text-emerald-300 text-lg font-semibold">+{claimed.credits_added} credits in!</p>
              <p className="text-gray-400 text-sm mt-1">Thanks @{claimed.from} 🎬 — go make something wild.</p>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button onClick={() => navigate("/dashboard?record=1")} className="bg-emerald-600 hover:bg-emerald-700">Record now</Button>
                <Link to="/"><Button variant="outline" className="border-gray-700 text-gray-200 w-full">Browse feed</Button></Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-center">{authIsLogin ? "Welcome back!" : "Create your account"}</DialogTitle>
            <DialogDescription className="text-center text-gray-400 text-sm">
              {authIsLogin ? "Sign in to claim your gift." : "Your gift will land in your account the moment you sign up."}
            </DialogDescription>
          </DialogHeader>
          <AuthForm isLogin={authIsLogin} onSuccess={() => setShowAuth(false)} />
          <div className="text-center text-sm text-gray-400">
            {authIsLogin ? "New here? " : "Already have an account? "}
            <button onClick={() => setAuthIsLogin(!authIsLogin)} className="text-emerald-400 hover:underline">
              {authIsLogin ? "Create one" : "Sign in"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftClaimPage;
